/* ================= CONSTANTS ================= */
const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';

/* ================= HELPERS ================= */

// CHANGED: Added viewport check to force animation
function isInViewport(el, margin = 200) {
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight + margin && rect.bottom > -margin;
}

function throttle(fn) {
  let ticking = false;
  return function (...args) {
    if (!ticking) {
      requestAnimationFrame(() => {
        fn.apply(this, args);
        ticking = false;
      });
      ticking = true;
    }
  };
}
// CHANGED - END

/* ================= SCROLL IN ANIMATION ================= */

// CHANGED: Simplified logic – animate ONCE, never re-hide
function onScrollIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    if (!entry.isIntersecting) return;

    const el = entry.target;

    el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

    if (el.hasAttribute('data-cascade')) {
      el.style.setProperty('--animation-order', index);
    }

    observer.unobserve(el);
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const elements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)
  );

  if (!elements.length) return;

  if (isDesignModeEvent) {
    elements.forEach(el =>
      el.classList.add('scroll-trigger--design-mode')
    );
    return;
  }

  const observer = new IntersectionObserver(onScrollIntersection, {
    threshold: 0,
    rootMargin: '200px 0px 200px 0px', 
  });

  elements.forEach(el => {
    if (isInViewport(el)) {
      el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    } else {
      el.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      el.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      observer.observe(el);
    }
  });
}
// CHANGED - END

/* ================= ZOOM IN ANIMATION ================= */

// CHANGED: More accurate visibility calculation
function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const rect = element.getBoundingClientRect();
  const elementHeight = element.offsetHeight;

  if (rect.top >= viewportHeight) return 0;
  if (rect.bottom <= 0) return 100;

  const visible = viewportHeight - rect.top;
  const total = viewportHeight + elementHeight;
  return Math.min(100, Math.max(0, Math.round((visible / total) * 100)));
}

function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const elements = Array.from(
    document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)
  );

  if (!elements.length) return;

  const scaleAmount = 0.2 / 100;

  elements.forEach(element => {
    let isVisible = false;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          isVisible = entry.isIntersecting;
        });
      },
      {
        threshold: 0,
        rootMargin: '200px 0px 200px 0px', 
      }
    );

    observer.observe(element);

    const updateZoom = () => {
      if (!isVisible) return;

      const ratio = 1 + scaleAmount * percentageSeen(element);
      element.style.setProperty('--zoom-in-ratio', ratio);
    };

    updateZoom();

    window.addEventListener(
      'scroll',
      throttle(updateZoom),
      { passive: true }
    );
  });
}
// CHANGED - END

/* ================= INIT ================= */
window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

/* ================= SHOPIFY SUPPORT ================= */
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', event => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeScrollZoomAnimationTrigger();
  });

  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeScrollZoomAnimationTrigger();
  });
}